// MUI から Box、Button、Grid、Typography コンポーネントをインポート
import { Box, Button, Grid, Typography } from '@mui/material';
import { useSnackbar } from 'notistack5';
// React とフックをインポート
import React, { useEffect, useState } from 'react';
// react-hook-form から useForm フックをインポート
import { useForm } from 'react-hook-form';
// react-i18next から useTranslation フックをインポート
import { useTranslation } from 'react-i18next';
import RHFonChangeTextField from 'src/components/react-hook-form/RHFonChangeTextField';
import { LIST_STATE_KEY_PREFIX } from 'src/constants';
import useLocales from 'src/hooks/useLocales';
import { useValidSaveButton } from '../hooks/useValidSaveButton';
// Redux ストアセレクターをインポート
import { useAppDispatch, useAppSelector } from 'src/redux/store';
import { useCalendarListGrid } from '../hooks/useCalendarListGrid';
import {
  setClearSearchFlag,
  setDay,
  setDayDisabled,
  setIsDestroyed,
  setIsLoading,
  setLastDay,
  setLastMonth,
  setLastSurveys,
  setLastYear,
  setMaxRow,
  setMonth,
  setMonthDisabled,
  setSurveyIds,
  setYear
} from '../store/enqueteCalendarSlice';
import RHFMultipleDropdown from './RHFMultipleDropdown';
// カレンダー管理画面上部の画面項目やボタンを表示するためのコンポーネント。
export const CalendarListPageCommonGrid: React.FC = () => {
  // 翻訳関数を取得
  const { t } = useTranslation();
  // Redux dispatch 関数を取得
  const dispatch = useAppDispatch();
  const hook = useCalendarListGrid();
  const methods = useForm({
    mode: 'onChange',
  });
  // Validator
  const { rulesSurvey } = useValidSaveButton(methods);
  // 現在の言語設定を取得
  const { currentLang } = useLocales();
  const { enqueueSnackbar } = useSnackbar();
  // 明細カレンダーデータ取得
  const surveyListData = useAppSelector((state) => state.enqueteCalendar.surveyListData);
  // アンケート詳細Idリスト
  const surveyIds = useAppSelector((state) => state.enqueteCalendar.surveyIds);
  // 検索条件：年
  const year = useAppSelector((state) => state.enqueteCalendar.year);
  // 検索条件：月
  const month = useAppSelector((state) => state.enqueteCalendar.month);
  // 検索条件：日
  const day = useAppSelector((state) => state.enqueteCalendar.day);
  // 現在のページ
  const page = useAppSelector((state) => state.enqueteCalendar.page);
  // 1ページ表示数
  const blockSize = useAppSelector((state) => state.enqueteCalendar.blockSize);
  const startPagePosition = useAppSelector((state) => state.enqueteCalendar.startPagePosition);
  const storageKey = useAppSelector((state) => state.enqueteCalendar.storageKey);
  // グリッドソートデータ
  const useSortModel = useAppSelector((state) => state.enqueteCalendar.useSortModel);
  // 検索条件：月の非活性フラグ
  const monthDisabled = useAppSelector((state) => state.enqueteCalendar.monthDisabled);
  // 検索条件：日の非活性フラグ
  const dayDisabled = useAppSelector((state) => state.enqueteCalendar.dayDisabled);
  // 前回検索条件 アンケート
  const lastSurveys = useAppSelector((state) => state.enqueteCalendar.lastSurveys);
  // 前回検索条件：年
  const lastYear = useAppSelector((state) => state.enqueteCalendar.lastYear);
  // 前回検索条件：月
  const lastMonth = useAppSelector((state) => state.enqueteCalendar.lastMonth);
  // 前回検索条件：日
  const lastDay = useAppSelector((state) => state.enqueteCalendar.lastDay);
  // 検索条件クリアフラグ
  const clearSearchFlag = useAppSelector((state) => state.enqueteCalendar.clearSearchFlag);

  const [initialized, setInitialized] = useState(false);

  //-------------------------------------------------------------
  // useEffect
  //-------------------------------------------------------------
  // 初回起動
  useEffect(() => {
    if(clearSearchFlag){
      dispatch(setLastSurveys([]));
      dispatch(setLastYear(''));
      dispatch(setLastMonth(''));
      dispatch(setLastDay(''));
    }else{
      // 検索条件クリアフラグ
      dispatch(setClearSearchFlag(true));
    }
    // アンケートデータ取得
    setsurveyListData();
    // 検索条件初期化
    // 検索条件：アンケート設定
    if(lastSurveys?.length && !lastSurveys?.includes('ALL') && !clearSearchFlag){
      methods.setValue('surveys', lastSurveys);
      dispatch(setSurveyIds(lastSurveys));
    }else{
      methods.setValue('surveys', ['ALL']);
      dispatch(setSurveyIds([]));
    }
    // 検索条件：年設定
    const currentYear = clearSearchFlag ? '' : lastYear;
    methods.setValue('year', currentYear);
    dispatch(setYear(currentYear));
    // 検索条件：月設定
    const currentMonth = clearSearchFlag ? '' : lastMonth;
    methods.setValue('month', currentMonth);
    dispatch(setMonth(currentMonth));
    dispatch(setMonthDisabled(!currentYear));
    // 検索条件：日設定
    const currentDay = clearSearchFlag ? '' : lastDay;
    methods.setValue('day', currentDay);
    dispatch(setDay(currentDay));
    dispatch(setDayDisabled(!currentMonth));
    
  }, []);

  useEffect(() => {
    destroyGrid();
  }, [currentLang]); // eslint-disable-line

  useEffect(() => {
    if (!initialized) return;
    // 全選択
    const allValue = surveyListData
      .filter((item) => item.surveyDetailId !== undefined && item.surveyDetailId !== null && item.surveyDetailId !== '')
      .map((item) => String(item.surveyDetailId));
    if (surveyIds.length === 0) dispatch(setSurveyIds(allValue));
    const filterModel = localStorage.getItem(`${LIST_STATE_KEY_PREFIX.FILTER}${storageKey}`);
    const filter = filterModel ?? {};
    // 最大件数データ取得
    setCountLimitedRowData(surveyIds.length === 0 ? allValue : surveyIds, year, month, day, filter, currentLang.code);
    // グリッドデータ取得
    setLimitedRowData(
      surveyIds.length === 0 ? allValue : surveyIds,
      year,
      month,
      day,
      blockSize,
      (page - startPagePosition) * blockSize,
      useSortModel,
      filter,
      currentLang.code,
    );
  }, [page, currentLang, initialized]);

  //アンケートデータ取得
  const setsurveyListData = async () => {
    try {
      // アンケートドロップダウンデータ取得
      await hook.getSurveyList();
    } finally {
      setInitialized(true);
    }
  };

  //-------------------------------------------------------------
  // 一覧検索
  //-------------------------------------------------------------
  const search = async () => {
    const valid = await methods.trigger();
    if (!valid) {
      // エラーある
      return;
    }
    const filterModel = localStorage.getItem(`${LIST_STATE_KEY_PREFIX.FILTER}${storageKey}`);
    const filter = filterModel ?? {};
    const dateString = getDateString(year, month, day);
    if (dateString !== '' && !isStrictDateString(dateString)) {
      // エラーメッセージ：正しい日付を入力してください。
      return enqueueSnackbar(t('enqueteCalendarList.message.validationErrorDate'), { variant: 'error' });
    }
    // 最大件数データ取得
    setCountLimitedRowData(surveyIds, year, month, day, filter, currentLang.code);
    destroyGrid();
    // グリッドデータ取得
    setLimitedRowData(surveyIds, year, month, day, blockSize, (page - startPagePosition) * blockSize, useSortModel, filter, currentLang.code);

    // 前回検索条件設定
    dispatch(setLastSurveys(methods.getValues('surveys')));
    dispatch(setLastYear(methods.getValues('year')));
    dispatch(setLastMonth(methods.getValues('month')));
    dispatch(setLastDay(methods.getValues('day')));
  };
  // 検索条件日付編集：年、月、日
  const getDateString = (year: string, month: string, day: string): string => {
    let dateStr = year;
    // 検索条件：月が入力の場合
    if (month.length > 0) {
      dateStr = dateStr + '-';
      if (month.length === 1) {
        dateStr = dateStr + '0';
      }
      dateStr += month;
    } else if (dateStr.length > 0) {
      // 検索条件：月が未入力の場合
      dateStr = dateStr + '-';
      dateStr = dateStr + '01';
    }
    // 検索条件：日が入力の場合
    if (day.length > 0) {
      dateStr = dateStr + '-';
      if (day.length === 1) {
        dateStr = dateStr + '0';
      }
      dateStr += day;
    } else if (dateStr.length > 0) {
      // 検索条件：日が未入力の場合
      dateStr = dateStr + '-';
      dateStr = dateStr + '01';
    }
    return dateStr;
  };
  // 検索条件日付入力チェック
  const isStrictDateString = (value: string): boolean => {
    const match = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return false;

    // 年(検索条件)
    const year = Number(match[1]);
    // 月(検索条件)
    const month = Number(match[2]);
    // 日(検索条件)
    const day = Number(match[3]);

    const date = new Date(year, month - 1, day);
    // 年(Date)
    const yyyy = date.getFullYear();
    // 月(Date)
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    // 日(Date)
    const dd = String(date.getDate()).padStart(2, '0');

    // 年月日(検索条件)と年月日(Date)比較
    const result = `${yyyy}-${mm}-${dd}`;
    return result === value;
  };

  //-------------------------------------------------------------
  // グリッドを再描画する
  //-------------------------------------------------------------
  const destroyGrid = () => {
    dispatch(setIsDestroyed(true));
    setTimeout(() => recreateGrid(), 0);
  };
  const recreateGrid = () => {
    dispatch(setIsDestroyed(false));
  };

  //最大件数データ取得
  const setCountLimitedRowData = async (
    // 選択したアンケートリスト
    surveyIds: string[],
    // 検索条件：年
    year: string,
    // 検索条件：月
    month: string,
    // 検索条件：日
    day: string,
    // フィルター条件
    useFiletrModel: { [key: string]: any },
    // 現在の言語
    currentLang: string,
  ) => {
    const rowCount = await hook.getCountLimitedLoadList(surveyIds, year, month, day, useFiletrModel, currentLang);
    dispatch(setMaxRow(rowCount !== undefined ? rowCount : 1));
  };
  //グリッドデータ取得
  const setLimitedRowData = async (
    // 選択したアンケートリスト
    surveyIds: string[],
    // 検索条件：年
    year: string,
    // 検索条件：月
    month: string,
    // 検索条件：日
    day: string,
    // 毎ページ件数
    blockSize: number,
    // 開始行
    startRow: number,
    // ソート条件
    useSortModel: any,
    // フィルター条件
    useFiletrModel: { [key: string]: any },
    // 現在の言語
    currentLang: string,
  ) => {
    dispatch(setIsLoading(true));
    try {
      //データ取得
      await hook.getLimitedLoadList(surveyIds, year, month, day, blockSize, startRow, useSortModel, useFiletrModel, currentLang);
    } finally {
      dispatch(setIsLoading(false));
    }
  };

  return (
    <>
      <Box component={'div'} sx={{ mb: 3 }}>
        <Grid container item direction="row" justifyContent="flex-start">
          {/** アンケート **/}
          <Grid item xs={1}>
            <Typography variant="subtitle1" style={{ marginTop: 8 }}>
              {t('enqueteCalendarList.searchCriteria.survey')}
            </Typography>
          </Grid>
          {/* 検索条件：アンケート */}
          <Grid item xs={6}>
            <RHFMultipleDropdown
              // フィールド名
              name={'surveys'}
              // フォーム制御オブジェクト
              methods={methods}
              // バリデーションルール
              rules={rulesSurvey}
              height={38 * 5}
              muiSize={'small'}
              // 変更処理関数
              handleOnChange={(value, name) => {
                if (value.includes('ALL')) {
                  const allValue = surveyListData
                    .filter((item) => item.surveyDetailId !== undefined && item.surveyDetailId !== null && item.surveyDetailId !== '')
                    .map((item) => String(item.surveyDetailId));
                  dispatch(setSurveyIds(allValue));
                } else {
                  dispatch(setSurveyIds(value));
                }
              }}
              options={surveyListData.map((item) => ({
                value: String(item.surveyDetailId),
                label: currentLang.value === 'ja' ? item.titleJpn : item.titleEng,
              }))}></RHFMultipleDropdown>
          </Grid>
        </Grid>
        {/** 年月日 **/}
        <Grid container item direction="row" justifyContent="flex-start">
          {/* 年 */}
          <Grid item xs={1}>
            <Typography variant="subtitle1">{t('enqueteCalendarList.searchCriteria.year')}</Typography>
          </Grid>
          {/* 月 */}
          <Grid item xs={1}>
            <Typography variant="subtitle1">{t('enqueteCalendarList.searchCriteria.month')}</Typography>
          </Grid>
          {/* 日 */}
          <Grid item xs={1}>
            <Typography variant="subtitle1">{t('enqueteCalendarList.searchCriteria.day')}</Typography>
          </Grid>
        </Grid>

        <Grid container item direction="row" justifyContent="flex-start">
          {/* 検索条件：年 */}
          <Grid item xs={1} id={`year`} key={`year`}>
            <RHFonChangeTextField
              // フィールド名
              name={`year`}
              // フォーム制御オブジェクト
              control={methods.control}
              size="small"
              // 表示スタイル
              variant="outlined"
              // 変更処理関数
              handleOnChange={(event) => {
                dispatch(setYear(event.target.value));
                if (event.target.value.length > 0) {
                  dispatch(setMonthDisabled(false));
                } else {
                  dispatch(setMonthDisabled(true));
                  dispatch(setDayDisabled(true));
                  methods.setValue('month', '');
                  dispatch(setMonth(''));
                  methods.setValue('day', '');
                  dispatch(setDay(''));
                  methods.clearErrors('month');
                  methods.clearErrors('day');
                }
              }}
              fullWidth
            />
          </Grid>
          {/* 検索条件：月 */}
          <Grid item xs={1} id={`month`} key={`month`}>
            <RHFonChangeTextField
              // フィールド名
              name={`month`}
              // フォーム制御オブジェクト
              control={methods.control}
              size="small"
              // 表示スタイル
              variant="outlined"
              // 無効化
              disabled={monthDisabled}
              // 変更処理関数
              handleOnChange={(event) => {
                dispatch(setMonth(event.target.value));
                if (event.target.value.length > 0) {
                  dispatch(setDayDisabled(false));
                } else {
                  dispatch(setDayDisabled(true));
                  methods.setValue('day', '');
                  methods.clearErrors('day');
                  dispatch(setDay(''));
                }
              }}
              fullWidth
            />
          </Grid>
          {/* 検索条件：日 */}
          <Grid item xs={1} id={`day`} key={`day`}>
            <RHFonChangeTextField
              // フィールド名
              name={`day`}
              // フォーム制御オブジェクト
              control={methods.control}
              // サイズ
              size="small"
              // 表示スタイル
              variant="outlined"
              // 無効化
              disabled={dayDisabled}
              // 変更処理関数
              handleOnChange={(event) => {
                dispatch(setDay(event.target.value));
              }}
              fullWidth
            />
          </Grid>
          {/* 検索ボタン */}
          <Grid item xs={4} display="flex" justifyContent="flex-end">
            <Button
              // 塗りつぶし
              variant={'contained'}
              // クリックイベント（検索）
              onClick={() => {
                search();
              }}
              // 子要素
              children={t('enqueteCalendarList.button.search')}
              sx={{ height: 40, width: 80 }}
              type="submit"
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
